import React from 'react';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';
import { CheckCircle, Package, Zap, Calculator, ShieldCheck, Car, Rocket, Building } from 'lucide-react'; // Icons for features
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useState, useMemo } from 'react'; // Added useState, useMemo
import { Helmet } from 'react-helmet-async'; // Import Helmet

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

// Placeholder data for packages - replace with actual details
const projectPackages = [
  {
    name: "Starter Website",
    price: "vanaf €749",
    description: "Perfect voor starters of KMO's die een professionele online aanwezigheid nodig hebben.",
    features: [
      "Uniek ontwerp op maat",
      "Mobielvriendelijk (Responsive)",
      "Basis SEO setup",
      "Contactformulier",
      "Tot 5 pagina's",
    ],
    cta: "Start Mijn Website",
    popular: false,
  },
  {
    name: "Business Groei",
    price: "vanaf €1499",
    description: "Uitgebreide website met focus op conversie, groei en geavanceerde functies.",
    features: [
      "Alles van Starter Website",
      "Geavanceerde SEO optimalisatie",
      "Blog / Nieuws module",
      "Integraties (bv. nieuwsbrief)",
      "Meer maatwerk mogelijk",
    ],
    cta: "Groei Online",
    popular: true, // Highlight this package
  },
  {
    name: "Oplossing op Maat",
    price: "Prijs op aanvraag",
    description: "Voor complexe projecten, webapplicaties of specifieke vereisten.",
    features: [
      "Volledig aangepast aan uw noden",
      "Webapplicaties",
      "E-commerce oplossingen",
      "API integraties",
      "Consultancy & strategie",
    ],
    cta: "Bespreek Uw Project",
    popular: false,
  },
];

const PricingPage = () => {
  // State for ROI Calculator
  const [avgLeadValue, setAvgLeadValue] = useState('150');
  const [extraLeads, setExtraLeads] = useState('5');
  const [investment, setInvestment] = useState('1499'); // Default to Business package

  const calculatedROI = useMemo(() => {
    const val = parseFloat(avgLeadValue) || 0;
    const leads = parseInt(extraLeads) || 0;
    const invest = parseFloat(investment) || 0;

    if (invest === 0) return { extraMonthlyRevenue: 0, roiPercentage: 0, paybackMonths: Infinity };

    const extraMonthlyRevenue = val * leads;
    const yearlyRevenue = extraMonthlyRevenue * 12;
    const roiPercentage = yearlyRevenue > 0 && invest > 0 ? ((yearlyRevenue - invest) / invest) * 100 : 0;
    const paybackMonths = extraMonthlyRevenue > 0 ? invest / extraMonthlyRevenue : Infinity;

    return {
      extraMonthlyRevenue,
      roiPercentage,
      paybackMonths,
    };
  }, [avgLeadValue, extraLeads, investment]);

  // Function to format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(value);
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-16 md:space-y-24"
    >
      <Helmet>
        <title>Prijzen Webdesign Pakketten & Onderhoud | CylostreamWeb Limburg</title>
        <meta name="description" content="Transparante prijzen voor professioneel webdesign in Limburg. Bekijk onze pakketten voor KMO's, starters en maatwerk. Inclusief website onderhoudsplan." />
        <meta name="keywords" content="prijzen webdesign, website kosten België, KMO website prijs, webdesign pakketten, website onderhoud kosten, CylostreamWeb Limburg" />
        <meta property="og:title" content="Prijzen Webdesign Pakketten & Onderhoud | CylostreamWeb Limburg" />
        <meta property="og:description" content="Transparante prijzen voor professioneel webdesign in Limburg. Bekijk onze pakketten voor KMO's, starters en maatwerk. Inclusief website onderhoudsplan." />
        <link rel="canonical" href="https://www.cylostream.com/pricing" /> 
      </Helmet>

      {/* Intro Section */}
      <motion.section variants={fadeInUp} className="text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Transparante <span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Prijzen</span>
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-2">
          Investeer in een website die resultaten oplevert. Wij bieden duidelijke pakketten en maatwerk voor elk budget en ambitie.
        </p>
        <p className="text-sm text-muted-foreground">(Alle prijzen zijn exclusief BTW)</p>
        <p className="text-sm text-muted-foreground mt-1">Geen verborgen kosten, de getoonde prijs is de eindprijs.</p>
        <p className="text-sm text-muted-foreground mt-4 font-medium bg-blue-50 p-3 rounded-md inline-block border border-blue-200">
          <span className="text-primary font-bold">Belangrijk:</span> Na de ontwikkeling van uw website dient u één van de onderstaande onderhoudsplannen te kiezen. 
          Elk plan geeft u toegang tot ons <span className="text-primary font-medium">CylostreamHub</span> klantenportaal.
        </p>
      </motion.section>

      {/* Project Packages Grid */}
      <motion.section variants={staggerContainer} className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        {projectPackages.map((pkg, index) => (
          <motion.div 
            key={index} 
            variants={fadeInUp} 
            className={`package-card border rounded-lg p-6 md:p-8 flex flex-col h-full ${pkg.popular ? 'border-primary border-2 relative bg-blue-50' : 'bg-card'}`}
          >
            {pkg.popular && (
              <div className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                Meest Gekozen
              </div>
            )}
            <Package size={32} className="mb-4 text-primary" />
            <h2 className="text-2xl font-semibold mb-2">
              {pkg.name === "Starter Website" && <><span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Starter</span> Website</>}
              {pkg.name === "Business Groei" && <><span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Business</span> Groei</>}
              {pkg.name === "Oplossing op Maat" && <><span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Oplossing</span> op Maat</>}
            </h2>
            <p className="text-3xl font-bold mb-4">{pkg.price}</p>
            <p className="text-muted-foreground text-sm mb-6 min-h-[3rem]">{pkg.description}</p>
            <ul className="space-y-2 mb-8 flex-grow">
              {pkg.features.map((feature, fIndex) => (
                <li key={fIndex} className="flex items-center text-sm">
                  <CheckCircle size={16} className="text-green-500 mr-2 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <Button asChild size="lg" className="w-full mt-auto" variant={pkg.popular ? "default" : "outline"}>
              <Link to="/contact">{pkg.cta}</Link>
            </Button>
          </motion.div>
        ))}
      </motion.section>

      {/* Mandatory Hosting & Security Section */}
      <motion.section variants={fadeInUp} className="mandatory-hosting-section bg-blue-50 p-8 md:p-12 rounded-lg border border-blue-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-2">
            <ShieldCheck size={32} className="mb-4 text-primary"/>
            <h2 className="text-3xl font-bold mb-4">
             <span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Premium EU Hosting & Veiligheid</span>
            </h2>
            <p className="text-muted-foreground mb-6">
              Net zoals een auto brandstof nodig heeft om te rijden en verzekering voor gemoedsrust, heeft uw website een solide basis nodig. Dit pakket is <strong>inbegrepen</strong> bij al onze abonnementen, gehost via <strong className='text-primary'>Cloudflare</strong>, wereldleider in webprestaties en veiligheid. Het garandeert optimale snelheid, beveiliging en ondersteuning binnen de EU.
            </p>
            <ul className="space-y-1 text-sm mb-6">
              <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> Premium EU Hosting (sneller & GDPR-conform)</li>
              <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> Hosting via Cloudflare (top prestaties & veiligheid)</li>
              <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> Automatische verlenging domein & hosting</li>
              <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> Dagelijkse backups</li>
              <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> Geavanceerde Firewall & Malware scanning</li>
              <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> Proactieve veiligheidsupdates</li>
              <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> Support binnen 48u (werkdagen)</li>
            </ul>
             <p className="text-xs text-muted-foreground">Dit pakket dekt de essentiële technische infrastructuur, basisveiligheid en domeinregistratie/-beheer. Andere hosting oplossingen zijn bespreekbaar.</p>
          </div>
          <div className="text-center md:text-right space-y-4">
            <div className="border p-4 rounded-md bg-background shadow-sm">
              <p className="text-sm text-muted-foreground">Inbegrepen bij alle plannen</p>
              <p className="text-xl font-bold text-primary">Premium EU Hosting</p>
              <p className="text-xl font-bold text-primary">& Beveiliging</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Maintenance Subscription Sections */}
      <motion.section variants={fadeInUp} className="bg-blue-50 border border-blue-200 rounded-lg p-6 md:p-8 mb-8">
        <h2 className="text-3xl font-bold text-center mb-6">
          <span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Onderhoudsplannen</span> (verplicht)
        </h2>
        <p className="text-muted-foreground text-center max-w-3xl mx-auto mb-8">
          Eén van deze plannen is verplicht voor het onderhoud van uw website. Alle plannen geven toegang tot ons CylostreamHub klantenportaal,
          waar u wijzigingen kunt aanvragen, facturen kunt bekijken en uw website kunt beheren.
        </p>
      </motion.section>

      <motion.section variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">

        {/* Op De Weg Plan */}
        <motion.div variants={fadeInUp} className="maintenance-card border rounded-lg p-6 md:p-8 flex flex-col bg-card">
          <Car size={32} className="mb-4 text-secondary"/>
          <h3 className="text-2xl font-semibold mb-2">
             <span className="inline-block bg-gradient-to-r from-secondary to-purple-400 bg-clip-text text-transparent">Op De Weg</span> Plan
          </h3>
          <p className="text-muted-foreground text-sm mb-4">De basis + extra gemak voor kleine aanpassingen.</p>
           <div className="border p-4 rounded-md bg-background mb-6 text-center shadow-sm">
             <p className="text-sm text-muted-foreground">Maandelijks tarief</p>
             <p className="text-3xl font-bold text-secondary">€99,99 / maand</p>
           </div>
          <ul className="space-y-2 mb-6 text-sm flex-grow">
             <li className="font-semibold">Inclusief Premium Hosting & Veiligheid, plus:</li>
             <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> <strong>Toegang tot CylostreamHub klantenportaal</strong></li>
             <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> Prioritaire ondersteuning (snellere respons)</li>
          </ul>
          <Button asChild size="lg" variant="secondary" className="w-full mt-auto">
              <Link to="/contact">Kies Op De Weg</Link>
          </Button>
        </motion.div>

        {/* Groei Zorgeloos Plan */}
        <motion.div variants={fadeInUp} className="maintenance-card border rounded-lg p-6 md:p-8 flex flex-col bg-card border-primary border-2 relative">
           <div className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                Populair
           </div>
          <Rocket size={32} className="mb-4 text-primary"/>
          <h3 className="text-2xl font-semibold mb-2">
             <span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Groei</span> Zorgeloos Plan
          </h3>
          <p className="text-muted-foreground text-sm mb-4">Voor bedrijven die actief willen groeien en verbeteren.</p>
          <div className="border p-4 rounded-md bg-background mb-6 text-center shadow-sm">
             <p className="text-sm text-muted-foreground">Maandelijks tarief</p>
             <p className="text-3xl font-bold text-primary">€199,99 / maand</p>
          </div>
          <ul className="space-y-2 mb-6 text-sm flex-grow">
              <li className="font-semibold">Alles van Op De Weg Plan, plus:</li>
              <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> <strong>Uitgebreide CylostreamHub portaal features</strong></li>
              <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> 2 uur change budget (content & design tweaks)</li>
              <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> Kwartaalelijkse SEO check & advies</li>
              <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> Core Web Vitals monitoring & basis optimalisatie</li>
          </ul>
          <Button asChild size="lg" className="w-full mt-auto">
              <Link to="/contact">Kies Groei Plan</Link>
          </Button>
        </motion.div>

        {/* Maatwerk / Enterprise Plan */}
        <motion.div variants={fadeInUp} className="maintenance-card border rounded-lg p-6 md:p-8 flex flex-col bg-card">
          <Building size={32} className="mb-4 text-gray-500"/>
           <h3 className="text-2xl font-semibold mb-2">
             <span className="inline-block bg-gradient-to-r from-gray-500 to-gray-700 bg-clip-text text-transparent">Maatwerk</span> / Enterprise
          </h3>
          <p className="text-muted-foreground text-sm mb-4">Volledig afgestemd op uw unieke bedrijfsdoelen.</p>
          <div className="border p-4 rounded-md bg-background mb-6 text-center shadow-sm">
             <p className="text-sm text-muted-foreground">Maandelijks tarief</p>
             <p className="text-3xl font-bold text-gray-600">vanaf €329,99</p>
          </div>
          <ul className="space-y-2 mb-6 text-sm flex-grow">
               <li className="font-semibold">Alles van Groei Zorgeloos Plan, plus:</li>
               <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> <strong>Premium CylostreamHub portaal met aanvullende tools</strong></li>
               <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> Tailored SLA & support kanalen</li>
               <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> Geavanceerde SEO & content strategie</li>
               <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> Optioneel: Advertentiebeheer (SEA)</li>
               <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> Optioneel: Meertalige SEO</li>
               <li className="flex items-center"><CheckCircle size={14} className="text-green-500 mr-2"/> Proactieve A/B testing & conversie optimalisatie</li>
          </ul>
           <Button asChild size="lg" variant="outline" className="w-full mt-auto">
               <Link to="/contact">Bespreek Maatwerk</Link>
           </Button>
        </motion.div>

      </motion.section>

      {/* ROI Calculator Section */}
      <motion.section variants={fadeInUp} className="roi-calculator bg-card border p-8 md:p-12 rounded-lg">
        <div className="text-center mb-8">
          <Calculator size={32} className="mx-auto mb-4 text-primary"/>
          <h2 className="text-3xl font-bold mb-2">
            Bereken Uw Potentiële <span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">ROI</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Schat in hoe snel een investering in een nieuwe website zichzelf kan terugverdienen door extra leads of klanten.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div>
            <Label htmlFor="avgLeadValue" className="text-sm font-medium">Gem. Waarde per Lead/Klant (€)</Label>
            <Input 
              id="avgLeadValue" 
              type="number" 
              value={avgLeadValue}
              onChange={(e) => setAvgLeadValue(e.target.value)}
              placeholder="bv. 150"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="extraLeads" className="text-sm font-medium">Verwachte Extra Leads/Klanten per Maand</Label>
            <Input 
              id="extraLeads" 
              type="number" 
              value={extraLeads}
              onChange={(e) => setExtraLeads(e.target.value)}
              placeholder="bv. 5"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="investment" className="text-sm font-medium">Uw Investering (€)</Label>
            <Input 
              id="investment" 
              type="number" 
              value={investment}
              onChange={(e) => setInvestment(e.target.value)}
              placeholder="bv. 1499"
              className="mt-1"
            />
          </div>
        </div>

        <div className="results-display bg-background border rounded-md p-6 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Extra Maandelijkse Omzet</p>
            <p className="text-2xl font-semibold">{formatCurrency(calculatedROI.extraMonthlyRevenue)}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Jaarlijkse ROI</p>
            <p className={`text-2xl font-semibold ${calculatedROI.roiPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {calculatedROI.roiPercentage.toFixed(0)}%
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Terugverdientijd</p>
            <p className="text-2xl font-semibold">
              {calculatedROI.paybackMonths === Infinity 
                ? "-"
                : `${calculatedROI.paybackMonths.toFixed(1)} Maanden`}
            </p>
          </div>
        </div>
         <p className="text-xs text-muted-foreground text-center mt-4">
           *Dit is een schatting. Werkelijke resultaten kunnen variëren.
         </p>
      </motion.section>

      {/* Addon / Custom Work Section - Optional */}
      <motion.section variants={fadeInUp} className="text-center">
        <h3 className="text-2xl font-semibold mb-2">Extra noden of maatwerk?</h3>
        <p className="text-muted-foreground mb-4 max-w-2xl mx-auto">
          Heeft u specifieke wensen zoals een webshop, API koppelingen of andere complexe functionaliteiten? Contacteer ons voor een offerte op maat. 
          Bent u ook geïnteresseerd in AI-gedreven oplossingen zoals chatbots of procesautomatisatie? Bezoek dan ook onze <a href="/original/" rel="noopener noreferrer" className="underline hover:text-primary">AI Services site</a>.
        </p>
        <Button asChild variant="outline">
          <Link to="/contact">Vraag Maatwerk Offerte</Link>
        </Button>
      </motion.section>

    </motion.div>
  );
};

export default PricingPage; 